import React from "react";
import { useDeviceDetection } from "../hooks/useDeviceDetection";

/** Shared back control for Add knowledge wizard steps (matches V2 browse back links). */
export function AddKnowledgeWizardBackButton({
  onClick,
  children,
  className = "",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 ${className}`.trim()}
    >
      <svg
        className="h-3.5 w-3.5 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      {children}
    </button>
  );
}

const EXTRACT_MODE_OPTIONS = [
  { value: "auto", label: "Auto", hint: "recommended" },
  { value: "force_on", label: "Force extract" },
  { value: "force_off", label: "Index only" },
];

/** Step 4 option: extraction mode after indexing (entity anchors only). Hidden on mobile (defaults to Auto). */
export function ExtractModeOption({ value = "auto", onChange, className = "" }) {
  const { isMobile } = useDeviceDetection();
  const selected = EXTRACT_MODE_OPTIONS.some((o) => o.value === value) ? value : "auto";

  if (isMobile) return null;

  return (
    <fieldset
      className={`rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 ${className}`.trim()}
    >
      <legend className="text-sm font-medium text-slate-900 px-0.5">
        Architecture extraction
      </legend>
      <div
        className="mt-1.5 inline-flex w-full flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1"
        role="radiogroup"
        aria-label="Architecture extraction mode"
      >
        {EXTRACT_MODE_OPTIONS.map((opt) => {
          const isActive = selected === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => onChange?.(opt.value)}
              className={`flex-1 min-w-[5.5rem] rounded-md px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-red-400/40 ${
                isActive
                  ? "bg-red-600 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              {opt.label}
              {opt.hint ? (
                <span className={`ml-1 font-normal ${isActive ? "text-red-100" : "text-slate-500"}`}>
                  ({opt.hint})
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <details className="mt-1.5 text-xs text-slate-600">
        <summary className="cursor-pointer font-medium text-slate-700">
          What these options mean
        </summary>
        <p className="mt-1.5 leading-relaxed">
          <strong className="text-slate-800">Auto</strong> lets DecisionPen decide from the content.
          {" "}
          <strong className="text-slate-800">Force extract</strong> always runs map extraction.
          {" "}
          <strong className="text-slate-800">Index only</strong> skips extraction and indexes for search.
        </p>
      </details>
    </fieldset>
  );
}
